import { useState, useCallback, useEffect, useRef } from 'react';
import type { DataProvider } from 'react-admin';

export interface EmployeeSearchResult {
  id: string;
  employeeNumber: string;
  demographics: {
    firstName?: string;
    lastName?: string;
    race: string;
    gender: string;
  };
  fullName: string;
  organisationalHierarchy: {
    department: string;
  };
  seniority: {
    occupationalLevel: string;
    jobTitle: string;
  };
  contractType: string;
  status: string;
}

interface UseEmployeeSearchOptions {
  dataProvider?: DataProvider;
  resource?: string;
  minCharsForSearch?: number;
  debounceMs?: number;
}

interface UseEmployeeSearchReturn {
  results: EmployeeSearchResult[];
  isLoading: boolean;
  error: string | null;
  search: (query: string) => void;
  clearResults: () => void;
}

export function useEmployeeSearch({
  dataProvider,
  resource = 'employees',
  minCharsForSearch = 2,
  debounceMs = 300,
}: UseEmployeeSearchOptions = {}): UseEmployeeSearchReturn {
  const [results, setResults] = useState<EmployeeSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dpRef = useRef(dataProvider);
  useEffect(() => {
    dpRef.current = dataProvider;
  }, [dataProvider]);

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  const search = useCallback(
    (query: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      const trimmedQuery = query.trim();

      if (!trimmedQuery || trimmedQuery.length < minCharsForSearch) {
        clearResults();
        return;
      }

      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        setError(null);

        try {
          const lowerQuery = trimmedQuery.toLowerCase();

          const response = await dpRef.current!.getList(resource, {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'lastName', order: 'ASC' },
            filter: {},
          });

          const allEmployees = response.data as Array<{ legalEntity?: { firstName?: string; lastName?: string }; employeeNumber?: string }>;
          const matched = allEmployees.filter((emp) => {
            const firstName = (emp.legalEntity?.firstName ?? '').toLowerCase();
            const lastName = (emp.legalEntity?.lastName ?? '').toLowerCase();
            const employeeNumber = (emp.employeeNumber ?? '').toLowerCase();

            return (
              firstName.includes(lowerQuery) ||
              lastName.includes(lowerQuery) ||
              employeeNumber.includes(lowerQuery)
            );
          });

          const searchResults: EmployeeSearchResult[] = matched.slice(0, 20).map((emp: { _id?: string; id?: string; employeeNumber?: string; legalEntity?: { firstName?: string; lastName?: string }; demographics?: { race?: string; gender?: string }; organisationalHierarchy?: { department?: string }; seniority?: { occupationalLevel?: string; jobTitle?: string }; contractType?: string; status?: string }) => ({
            id: emp._id ?? emp.id ?? '',
            employeeNumber: emp.employeeNumber ?? '',
            demographics: {
              firstName: emp.legalEntity?.firstName ?? '',
              lastName: emp.legalEntity?.lastName ?? '',
              race: emp.demographics?.race ?? '',
              gender: emp.demographics?.gender ?? '',
            },
            fullName: `${emp.legalEntity?.firstName ?? ''} ${emp.legalEntity?.lastName ?? ''}`.trim(),
            organisationalHierarchy: { department: emp.organisationalHierarchy?.department ?? '' },
            seniority: {
              occupationalLevel: emp.seniority?.occupationalLevel ?? '',
              jobTitle: emp.seniority?.jobTitle ?? '',
            },
            contractType: emp.contractType ?? '',
            status: emp.status ?? '',
          }));

          setResults(searchResults);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to search employees');
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    },
    [resource, minCharsForSearch, debounceMs, clearResults]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  return {
    results,
    isLoading,
    error,
    search,
    clearResults,
  };
}